import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { notifyError } from "../lib/ui";
import { createInvoiceLink, getConfig, getReceipt, me, myOrders } from "../api/endpoints";
import { contactManager } from "../lib/contact";
import { haptic } from "../lib/tg";
import { api } from "../api/client";
import ProductPhoto from "../components/ProductPhoto";
import Countdown from "../components/Countdown";
import { ReceiptSheet, ReservationSheet } from "../components/Voucher";
import { HandBlot } from "../components/Hand";
import { useTheme } from "../hooks/useTheme";
import { isTelegram } from "../lib/platform";
import { useUserStore } from "../store/user";
import type { Gender, Order, Receipt, User } from "../types";
import { categoryLabel } from "../types";

const CLOTHING_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
const SHOE_SIZES = ["36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47"];

function OrderCard({ order, onPaid, autoOpen }: { order: Order; onPaid: () => void; autoOpen?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [talonOpen, setTalonOpen] = useState(false);

  // Пришли по ссылке из уведомления бота — сразу показываем документ, ради
  // которого человек тапнул: чек у продажи, талон у активной брони.
  useEffect(() => {
    if (!autoOpen) return;
    if (order.paid_at || order.status === "issued") {
      getReceipt(order.id).then(setReceipt).catch(() => {});
    } else if (order.type === "reservation") {
      setTalonOpen(true);
    }
  }, [autoOpen, order.id, order.paid_at, order.status, order.type]);
  const p = order.product;

  const needsDeposit =
    order.type === "reservation" &&
    order.deposit_amount > 0 &&
    order.deposit_status === "none" &&
    order.status === "pending";

  const statusLabel: Record<string, string> = {
    pending: "Ожидает выдачи",
    issued: "Выдан",
    expired: "Истёк",
    cancelled: "Отменён",
  };

  const payDeposit = async () => {
    setBusy(true);
    try {
      const { link } = await createInvoiceLink(order.id);
      const tg = window.Telegram?.WebApp;
      if (tg?.openInvoice) {
        tg.openInvoice(link, (s) => { if (s === "paid") onPaid(); });
      } else {
        window.open(link, "_blank");
      }
    } catch (e) {
      notifyError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: "var(--secondary-bg)", borderRadius: 14, marginBottom: 10, overflow: "hidden" }}>
      {/* Карточка товара */}
      <Link to={`/product/${p.id}`} style={{ display: "flex", gap: 12, padding: 12 }}>
        <ProductPhoto productId={p.id} alt={p.title ?? p.sku} size={72} variant="thumb" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: "0 0 2px", fontWeight: 600, fontSize: 14 }}>
            {p.title ?? categoryLabel(p.category)}
          </p>
          <p className="micro" style={{ margin: "0 0 5px", color: "var(--hint)" }}>
            {p.size} · #{p.sku}
          </p>
          <p style={{ margin: 0, fontWeight: 700, color: "var(--accent)" }}>
            {Math.round(p.price).toLocaleString("ru-RU")} ₽
          </p>
        </div>
        <span className="micro" style={{ color: "var(--hint)", flexShrink: 0, alignSelf: "flex-start", marginTop: 3 }}>
          {statusLabel[order.status] ?? order.status}
        </span>
      </Link>

      {/* Детали заказа */}
      <div style={{ padding: "0 12px 12px", borderTop: "1px solid var(--border)" }}>
        <p className="micro" style={{ margin: "9px 0 0", color: "var(--hint)" }}>
          {order.type === "purchase" ? "Покупка" : "Бронь"} #{order.id}
          {order.deposit_amount > 0 && ` · залог ${Math.round(order.deposit_amount).toLocaleString("ru-RU")} ₽${order.deposit_status === "paid" ? " (оплачен)" : ""}`}
        </p>
        {order.expires_at && order.status === "pending" && (
          <p style={{ margin: "4px 0 0", fontSize: 13 }}>
            <Countdown expiresAt={order.expires_at} />
            <span style={{ color: "var(--hint)", marginLeft: 6 }}>
              (до {new Date(order.expires_at).toLocaleString("ru-RU", {
                day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
              })})
            </span>
          </p>
        )}
        {needsDeposit && (
          <button className="btn" style={{ marginTop: 8 }} disabled={busy} onClick={payDeposit}>
            Оплатить залог
          </button>
        )}

        {/* Документы заказа: чек живёт у клиента столько же, сколько сам заказ,
            а не только в момент оплаты. Талон брони нужен на выдаче. */}
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {/* Чек есть и у выданного заказа: оплата при получении — тоже продажа. */}
          {(order.paid_at || order.status === "issued") && (
            <button
              className="btn btn--secondary"
              style={{ width: "auto", padding: "8px 14px", fontSize: 13 }}
              onClick={() => { haptic.tap(); getReceipt(order.id).then(setReceipt).catch(notifyError); }}
            >
              Чек
            </button>
          )}
          {order.type === "reservation" && (
            <button
              className="btn btn--secondary"
              style={{ width: "auto", padding: "8px 14px", fontSize: 13 }}
              onClick={() => { haptic.tap(); setTalonOpen(true); }}
            >
              Талон брони
            </button>
          )}
        </div>
      </div>

      {receipt && <ReceiptSheet receipt={receipt} onClose={() => setReceipt(null)} />}
      {talonOpen && <ReservationSheet order={order} onClose={() => setTalonOpen(false)} />}
    </div>
  );
}

interface ProfilePageProps {
  onOpenRoleSwitcher?: () => void;
  viewAsLabel?: string;
  /** Сотрудник: нижняя навигация занята рабочими разделами, «Избранное» — строкой здесь. */
  isStaff?: boolean;
  /** Владелец/админ: управление пользователями строкой, а не шестой вкладкой навигации. */
  canManageUsers?: boolean;
}

export default function ProfilePage({ onOpenRoleSwitcher, viewAsLabel, isStaff, canManageUsers }: ProfilePageProps) {
  // ?order=<id> приходит из диплинка бота (?startapp=order_<id>).
  const [searchParams] = useSearchParams();
  const targetOrderId = Number(searchParams.get("order")) || null;
  const { theme, toggleTheme } = useTheme();
  const reloadUser = useUserStore((s) => s.load);
  const logout = useUserStore((s) => s.logout);
  const [userData, setUserData] = useState<User | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [tab, setTab] = useState<"purchases" | "reservations">("purchases");
  const [paramsOpen, setParamsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [gender, setGender] = useState<Gender | "">("");
  const [height, setHeight] = useState("");
  const [shoeSize, setShoeSize] = useState("");
  const [clothingSize, setClothingSize] = useState("");
  const [supportUrl, setSupportUrl] = useState<string | null>(null);

  useEffect(() => { getConfig().then((c) => setSupportUrl(c.support_url)).catch(() => {}); }, []);

  const loadData = () => {
    me().then((u) => {
      setUserData(u);
      setGender(u.gender ?? "");
      setHeight(u.height ? String(u.height) : "");
      setShoeSize(u.shoe_size ?? "");
      setClothingSize(u.clothing_size ?? "");
    });
    myOrders().then(setOrders);
  };

  useEffect(() => { loadData(); }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api<User>("/me", {
        method: "PATCH",
        body: JSON.stringify({
          gender: gender || null,
          height: height ? Number(height) : null,
          shoe_size: shoeSize || null,
          clothing_size: clothingSize || null,
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      // Обновляем глобальный стор — чтобы "Мой размер" в каталоге сразу работал
      reloadUser();
    } finally {
      setSaving(false);
    }
  };

  const purchases    = orders.filter((o) => o.type === "purchase");
  const reservations = orders.filter((o) => o.type === "reservation");

  // Заказ из ссылки может лежать на другой вкладке — переключаемся на неё,
  // иначе документ открылся бы в скрытом поддереве и его никто не увидит.
  useEffect(() => {
    if (!targetOrderId) return;
    const target = orders.find((o) => o.id === targetOrderId);
    if (target) setTab(target.type === "purchase" ? "purchases" : "reservations");
  }, [targetOrderId, orders]);

  const initials = userData?.first_name?.slice(0, 1).toUpperCase() ?? "?";
  const displayName = userData?.first_name ?? "Пользователь";
  const username = userData?.username ? `@${userData.username}` : null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const photoUrl: string | null = (window.Telegram?.WebApp as any)?.initDataUnsafe?.user?.photo_url ?? null;

  // Краткое описание параметров для свёрнутого состояния
  const paramsSummary = [
    gender === "male" ? "Муж." : gender === "female" ? "Жен." : null,
    clothingSize || null,
    shoeSize ? `обувь ${shoeSize}` : null,
    height ? `${height} см` : null,
  ].filter(Boolean).join(" · ");

  return (
    <div className="container">

      {/* Шапка профиля */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 0 20px" }}>
        <div style={{
          width: 56, height: 56, borderRadius: "50%",
          background: "var(--accent)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 22, fontWeight: 700, color: "#fff", flexShrink: 0,
          overflow: "hidden",
        }}>
          {photoUrl
            ? <img src={photoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : initials
          }
        </div>
        {/* Имя — дисплейный слой экрана: у профиля своего h1 не было, и рука
            здесь не звучала. Длинное имя переносится по словам, кегль ниже
            34px опускать нельзя — рука пропадёт. */}
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, overflowWrap: "anywhere" }}>{displayName}</h1>
          {username && <p className="micro" style={{ margin: "4px 0 0", color: "var(--hint)" }}>{username}</p>}
        </div>
      </div>

      {/* Параметры — сворачиваемый блок */}
      <div style={{ background: "var(--secondary-bg)", borderRadius: 16, marginBottom: 20, overflow: "hidden" }}>
        <button
          onClick={() => setParamsOpen((v) => !v)}
          style={{
            width: "100%", padding: "14px 16px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text)",
          }}
        >
          <div style={{ textAlign: "left" }}>
            <p className="section-label" style={{ margin: 0 }}>Мои параметры</p>
            {!paramsOpen && paramsSummary && (
              <p style={{ margin: "2px 0 0", fontSize: 13, color: "var(--hint)" }}>{paramsSummary}</p>
            )}
            {!paramsOpen && !paramsSummary && (
              <p style={{ margin: "2px 0 0", fontSize: 13, color: "var(--hint)" }}>Не заполнено</p>
            )}
          </div>
          <span style={{ fontSize: 18, color: "var(--hint)", transition: "transform 0.2s", display: "inline-block", transform: paramsOpen ? "rotate(180deg)" : "none" }}>
            ›
          </span>
        </button>

        {paramsOpen && (
          <div style={{ padding: "0 16px 16px" }}>
            {/* Пол */}
            <div className="field">
              <label>Пол</label>
              <div style={{ display: "flex", gap: 8 }}>
                {([["male", "Мужской"], ["female", "Женский"]] as [Gender, string][]).map(([val, label]) => (
                  <button
                    key={val}
                    onClick={() => setGender(gender === val ? "" : val)}
                    style={{
                      flex: 1, padding: "10px 0", borderRadius: 10, border: "1px solid",
                      borderColor: gender === val ? "var(--accent)" : "var(--border)",
                      background: gender === val ? "var(--accent)" : "var(--bg)",
                      color: gender === val ? "#fff" : "var(--text)",
                      fontWeight: gender === val ? 600 : 400,
                      fontSize: 14, cursor: "pointer",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <label>Рост (см)</label>
              <input
                type="number"
                inputMode="numeric"
                placeholder="например 178"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
              />
            </div>

            <div className="field">
              <label>Размер одежды</label>
              <select value={clothingSize} onChange={(e) => setClothingSize(e.target.value)}>
                <option value="">Не указан</option>
                {CLOTHING_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="field">
              <label>Размер обуви (EU)</label>
              <select value={shoeSize} onChange={(e) => setShoeSize(e.target.value)}>
                <option value="">Не указан</option>
                {SHOE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <button className="btn" onClick={saveProfile} disabled={saving}>
              {saved ? "Сохранено ✓" : saving ? "Сохраняем…" : "Сохранить"}
            </button>
          </div>
        )}
      </div>

      {/* Вкладки */}
      <div className="tabs" style={{ marginBottom: 12 }}>
        <button className={tab === "purchases" ? "active" : ""} onClick={() => setTab("purchases")}>
          Покупки{purchases.length > 0 ? ` (${purchases.length})` : ""}
        </button>
        <button className={tab === "reservations" ? "active" : ""} onClick={() => setTab("reservations")}>
          Брони{reservations.length > 0 ? ` (${reservations.length})` : ""}
        </button>
      </div>

      {tab === "purchases" && (
        purchases.length === 0
          ? <div className="empty"><HandBlot className="empty__blot" /><p style={{ color: "var(--hint)", margin: 0 }}>Покупок пока нет.</p></div>
          : purchases.map((o) => <OrderCard key={o.id} order={o} onPaid={loadData} autoOpen={o.id === targetOrderId} />)
      )}

      {tab === "reservations" && (
        reservations.length === 0
          ? <div className="empty"><HandBlot variant={2} className="empty__blot" /><p style={{ color: "var(--hint)", margin: 0 }}>Броней пока нет.</p></div>
          : reservations.map((o) => <OrderCard key={o.id} order={o} onPaid={loadData} autoOpen={o.id === targetOrderId} />)
      )}

      {/* Разделы, которым не хватило места в нижней навигации. */}
      <div style={{ marginTop: 24, background: "var(--secondary-bg)", borderRadius: 16, overflow: "hidden" }}>
        <Link to="/subscriptions" className="row-link">
          <span className="micro">🔔 Подписки на новинки</span>
          <span style={{ color: "var(--hint)" }}>›</span>
        </Link>
        {isStaff && (
          <Link to="/likes" className="row-link">
            <span className="micro">♥ Избранное</span>
            <span style={{ color: "var(--hint)" }}>›</span>
          </Link>
        )}
        {canManageUsers && (
          <Link to="/admin" className="row-link">
            <span className="micro">Пользователи</span>
            <span style={{ color: "var(--hint)" }}>›</span>
          </Link>
        )}
      </div>

      {/* Связь с менеджером */}
      {supportUrl && (
        <button
          className="btn btn--secondary"
          style={{ marginTop: 24 }}
          onClick={() => contactManager(supportUrl)}
        >
          💬 Связаться с менеджером
        </button>
      )}

      {/* Настройки */}
      <div style={{ marginTop: 20, background: "var(--secondary-bg)", borderRadius: 16, overflow: "hidden" }}>
        <button
          onClick={() => { haptic.select(); toggleTheme(); }}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            width: "100%", padding: "14px 16px",
            background: "none", border: "none", borderBottom: onOpenRoleSwitcher ? "1px solid var(--border)" : "none",
            color: "var(--text)", cursor: "pointer", textAlign: "left",
          }}
        >
          <span className="micro">Тема оформления</span>
          <span className="micro" style={{ color: "var(--hint)" }}>{theme === "dark" ? "Тёмная ☀️" : "Светлая 🌙"}</span>
        </button>

        {onOpenRoleSwitcher && (
          <button
            onClick={onOpenRoleSwitcher}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              width: "100%", padding: "14px 16px",
              background: "none", border: "none",
              color: "var(--text)", cursor: "pointer", textAlign: "left",
            }}
          >
            <span className="micro">Просматривать как</span>
            <span className="micro" style={{ color: "var(--hint)" }}>{viewAsLabel ?? "Админ"}</span>
          </button>
        )}
      </div>

      {/* Выход — только для веб-сессии (в Telegram аккаунт привязан к initData). */}
      {!isTelegram() && (
        <button
          className="btn btn--secondary"
          style={{ marginTop: 16 }}
          onClick={logout}
        >
          Выйти
        </button>
      )}
    </div>
  );
}
